import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Search, Users, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { eventApi, markCheckedIn } from "@/lib/eventHub";
import EventLayout from "@/components/event/EventLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Guest-of-Host public flow (Section 3 of the spec).
 * - Renders a searchable host picker (no QR codes)
 * - Collects guest info
 * - Calls POST /event/checkin/group to atomically claim + check in
 * - Handles "allocation exhausted" gracefully with volunteer-assistance messaging
 *
 * Usage: standalone route (/event/guest-checkin) OR embedded step inside CheckIn.jsx.
 * When `mode === "preregister"` the API is called with check_in_now=false.
 * When URL param `?host_id=xxx` is present, the host picker is skipped and the
 * form opens with that host pre-selected (used by CheckIn.jsx when a searched
 * attendee is detected as a host and the arrival tap "I'm their guest").
 */
export default function GuestOfHost({ mode = "checkin" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectHostId = searchParams.get("host_id") || "";
  const preselectDay = searchParams.get("day") || "";
  const [step, setStep] = useState(1); // 1=host, 2=guest form, 3=success
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [host, setHost] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    event_day: (preselectDay === "2026-07-17" || preselectDay === "2026-07-18") ? preselectDay : "2026-07-18",
    organization: "", city: "", state: "",
    consent_sms: true, consent_email: false,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data } = await eventApi.listHosts();
        if (m) {
          const list = data.hosts || [];
          setHosts(list);
          // Deep-link: if a host_id was passed in the URL, jump directly to
          // step 2 with that host locked in (used by search → "I'm the guest"
          // flow from CheckIn.jsx).
          if (preselectHostId) {
            const picked = list.find((h) => h.id === preselectHostId);
            if (picked) {
              setHost(picked);
              setStep(2);
            }
          }
        }
      } catch (e) { /* noop */ }
      if (m) setLoading(false);
    })();
    return () => { m = false; };
  }, [preselectHostId]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return hosts;
    return hosts.filter((h) =>
      (h.name || "").toLowerCase().includes(needle) ||
      (h.organization || "").toLowerCase().includes(needle),
    );
  }, [q, hosts]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!host || !form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = { ...form, host_id: host.id, check_in_now: mode !== "preregister" };
      const { data } = await eventApi.groupCheckin(body);
      setResult(data);
      setStep(3);
      if (mode !== "preregister") {
        markCheckedIn({
          attendee_id: data?.attendee?.id,
          name: `${form.first_name} ${form.last_name}`,
          event_day: form.event_day,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Something went wrong.");
      setError({
        message: msg,
        exhausted: detail?.error === "no_tickets_remaining",
        not_open: detail?.error === "check_in_not_open",
        closed: detail?.error === "check_in_closed",
      });
    }
    setBusy(false);
  };

  return (
    <EventLayout showProgramBanner={false} className="bg-gradient-to-b from-white via-emerald-50/40 to-white">
      <div className="mx-auto max-w-lg px-5 py-8">
        {/* Header */}
        <button
          onClick={() => {
            if (step === 3) { navigate("/event/checkin"); return; }
            // Deep-linked from CheckIn.jsx (?host_id=xxx) → go back to search
            // instead of showing the host picker they intentionally skipped.
            if (step === 2 && preselectHostId) { navigate("/event/checkin"); return; }
            if (step === 1) { navigate("/event/checkin"); return; }
            setStep(step - 1);
          }}
          data-testid="guest-back"
          className="inline-flex items-center gap-1 text-xs font-[Outfit] font-bold uppercase tracking-widest text-emerald-800/60 hover:text-fuchsia-700 mb-4"
        >
          <ChevronLeft size={14} /> Back
        </button>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 px-3 py-1 mb-3">
          <Users size={12} className="text-fuchsia-700" />
          <span className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-800">
            {mode === "preregister" ? "Pre-Register as a Guest" : "Guest of a Host"}
          </span>
        </div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-[1.02] text-emerald-950">
          {step === 1 && "Who invited you?"}
          {step === 2 && "Tell us about yourself"}
          {step === 3 && (mode === "preregister" ? "You’re on the list" : "You’re checked in!")}
        </h1>
        <p className="mt-2 font-[Outfit] text-sm text-emerald-950/60">
          {step === 1 && "Tap the host, sponsor, or organization that invited you tonight."}
          {step === 2 && `Just a few details so ${host?.name || "your host"} can welcome you.`}
          {step === 3 && "Please have your name ready at the registration desk."}
        </p>

        {/* STEP 1 · Host picker */}
        {step === 1 && (
          <div className="mt-6 space-y-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-950/40" />
              <Input
                data-testid="host-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search host or organization…"
                className="pl-9 h-11 rounded-full font-[Outfit]"
              />
            </div>
            {loading ? (
              <div className="p-6 text-center text-emerald-800/60"><Loader2 className="mx-auto animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-emerald-950/10 bg-white p-6 text-center text-sm font-[Outfit] text-emerald-800/60">
                No hosts match your search.
              </div>
            ) : (
              <ul className="space-y-2">
                {filtered.map((h) => (
                  <li key={h.id}>
                    <button
                      data-testid={`host-option-${h.id}`}
                      onClick={() => { setHost(h); setStep(2); }}
                      className="w-full text-left rounded-2xl border-2 border-emerald-100 bg-white hover:border-fuchsia-300 hover:bg-fuchsia-50/40 px-4 py-3 flex items-center justify-between transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="font-[Cormorant_Garamond] text-xl text-emerald-950 leading-tight">{h.name}</div>
                        {h.organization && (
                          <div className="text-xs font-[Outfit] text-emerald-950/60 truncate">{h.organization}</div>
                        )}
                      </div>
                      <span className={`text-[10px] font-[Outfit] font-bold uppercase tracking-widest shrink-0 px-2 py-1 rounded-full ${
                        h.remaining > 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                      }`}>
                        {h.remaining} left
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* STEP 2 · Guest form */}
        {step === 2 && host && (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <div className="rounded-2xl bg-emerald-950 text-white px-4 py-3">
              <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-300">Invited by</div>
              <div className="font-[Cormorant_Garamond] text-xl">{host.name}</div>
              {host.organization && <div className="text-[11px] font-[Outfit] text-white/60">{host.organization}</div>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <FormField label="First name *" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="guest-first" required />
              <FormField label="Last name *" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="guest-last" required />
            </div>
            <FormField label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="guest-email" required />
            <FormField label="Mobile phone *" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="guest-phone" required />
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Event day * (attending both nights? Register separately for each)</span>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[
                  { v: "2026-07-17", l: "Friday · Jul 17", sub: "4:30–8:30 PM" },
                  { v: "2026-07-18", l: "Saturday · Jul 18", sub: "1:00–5:30 PM" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.v}
                    data-testid={`guest-day-${opt.v}`}
                    onClick={() => setForm({ ...form, event_day: opt.v })}
                    className={`rounded-xl py-2.5 px-2 font-[Outfit] font-bold text-sm transition-all ${
                      form.event_day === opt.v
                        ? "bg-emerald-950 text-white shadow-md"
                        : "bg-white border-2 border-emerald-100 text-emerald-950"
                    }`}
                  >
                    <div>{opt.l}</div>
                    <div className={`text-[10px] font-normal ${form.event_day === opt.v ? "text-fuchsia-300" : "text-emerald-950/50"}`}>{opt.sub}</div>
                  </button>
                ))}
              </div>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Business or org (opt.)" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} />
              <FormField label="City (opt.)" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            </div>

            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-2">
              <label className="flex items-start gap-2 text-xs font-[Outfit] text-emerald-900 leading-relaxed">
                <input
                  data-testid="guest-consent-sms"
                  type="checkbox"
                  checked={form.consent_sms}
                  onChange={(e) => setForm({ ...form, consent_sms: e.target.checked })}
                  className="h-4 w-4 mt-0.5 rounded"
                />
                <span>
                  <strong>SMS consent (optional):</strong> Send me event-day text messages (welcome + reminders).
                  Msg &amp; data rates may apply. Reply STOP to opt out.
                  See our <a href="/privacy" className="underline">Privacy</a> &amp; <a href="/terms" className="underline">Terms</a>.
                </span>
              </label>
              <label className="flex items-start gap-2 text-xs font-[Outfit] text-emerald-900 leading-relaxed">
                <input
                  data-testid="guest-consent-email"
                  type="checkbox"
                  checked={form.consent_email}
                  onChange={(e) => setForm({ ...form, consent_email: e.target.checked })}
                  className="h-4 w-4 mt-0.5 rounded"
                />
                <span>Email me event updates + the post-event survey and photo gallery.</span>
              </label>
            </div>

            {error && (
              <div
                data-testid="guest-error"
                className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-sm font-[Outfit] text-amber-900 flex gap-2"
              >
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>
                  {error.message}
                  {error.exhausted && (
                    <span className="block mt-2 text-xs">
                      Please step over to a registration volunteer — they can approve a complimentary ticket, assign you to another host, or register you as a walk-in.
                    </span>
                  )}
                </span>
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="guest-submit"
              className="w-full h-12 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-[Outfit] font-bold text-sm shadow-lg shadow-fuchsia-500/30 mt-2"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : (mode === "preregister" ? "Save my spot" : "Complete check-in")}
            </Button>
          </form>
        )}

        {/* STEP 3 · Success */}
        {step === 3 && result && (
          <div data-testid="guest-success" className="mt-6 rounded-3xl bg-emerald-950 text-white p-6 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-fuchsia-500/30 blur-3xl" />
            <div className="relative">
              <CheckCircle2 size={40} className="text-emerald-300 mb-3" />
              <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-300">
                {mode === "preregister" ? "Registration Saved" : "Successfully Checked In"}
              </div>
              <div className="font-[Cormorant_Garamond] text-3xl leading-tight mt-1">
                {result.attendee?.first_name} {result.attendee?.last_name}
              </div>
              <div className="text-sm font-[Outfit] text-white/70 mt-1">
                Guest of {result.host?.name}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button
                  onClick={() => navigate("/event/program")}
                  data-testid="guest-success-program"
                  className="rounded-full h-11 bg-white text-emerald-950 hover:bg-emerald-100 font-[Outfit] font-bold text-sm"
                >
                  Open Program
                </Button>
                <Button
                  onClick={() => navigate("/event")}
                  data-testid="guest-success-home"
                  className="rounded-full h-11 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-[Outfit] font-bold text-sm"
                >
                  Event Home
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </EventLayout>
  );
}

function FormField({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">{label}</span>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1 h-11 rounded-md font-[Outfit]"
      />
    </label>
  );
}
